import React, { Fragment, useState, useEffect, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  Card,
  CardBody
} from "reactstrap";
import FieldWrapper from "../commons/FieldWrapper";
import ApplicantData from "../applicant/Data";
import { saveStage } from "./Axios";
import AppointmentForm from "../interview/appointments/Form";
import DocumentationRequestForm from "../documentation/requests/Form";
import AdminUploadDocumentsForm from "../documentation/upload/AdminUploadDocumentsForm";
import CustomFieldsForm from "../custom_fields/CustomFieldsForm";

const StagesForm = ({
                      stage: initialStage,
                      applicant,
                      courseId,
                      postulationId,
                      setApplicanttHit,
                      showNotification,
                      isEditing = false,
                      toggleModal,
                      reloadQuery
                    }) => {
  const [modal, setModal] = useState(isEditing);
  const [observations, setObservations] = useState(initialStage.observations || "");
  const [switchState, setSwitchState] = useState(initialStage.aasm_state || "");
  const [aasmErrors, setAasmErrors] = useState([]);
  const customFieldsRef = useRef(null);

  useEffect(() => {
    setObservations(initialStage.observations || "");
    setSwitchState(initialStage.aasm_state || "");
  }, [initialStage]);

  const toggle = () => {
    if (isEditing && toggleModal) {
      toggleModal();
    } else {
      setModal((prevModal) => !prevModal);
    }
  };

  const handleSubmit = async () => {
    try {
      if (customFieldsRef.current) {
        await customFieldsRef.current.saveCustomFields();
      }

      let data = new FormData();
      data.append("stage[observations]", observations);
      data.append("stage[aasm_state]", switchState || initialStage.aasm_state);

      saveStage(courseId, postulationId, initialStage.id, data, (response) => {
        if (response?.status === 200) {
          setModal(false);
          setApplicanttHit(response.data);
          showNotification("Genial", "Información guardada con éxito!", "success");
          if (isEditing && toggleModal) {
            toggleModal();
          }
          reloadQuery();
        } else if (response?.status === 422) {
          setAasmErrors(response.data.aasm_state);
          showNotification("Ops", "Hubo un error, revisa el formulario!", "danger");
        }
      });

    } catch (error) {
      showNotification("Error", "No se pudieron guardar los cambios", "danger");
    }
  };



  const adminUploadDocumentsForm = useMemo(() => {
    return (
      <AdminUploadDocumentsForm
        stageId={initialStage.id}
        setApplicanttHit={setApplicanttHit}
        showNotification={showNotification}
        applicant={applicant}
        toggle={toggle}
      />
    );
  }, [initialStage.id, setApplicanttHit, showNotification, applicant]);



  const drawCustomStageForm = () => {
    switch (initialStage.kind) {
      case "document_request":
        return (
          <Fragment>
            <hr />
            <DocumentationRequestForm
              stage={initialStage}
              setApplicanttHit={setApplicanttHit}
              showNotification={showNotification}
              applicant={applicant}
              toggle={toggle}
            />
          </Fragment>
        );
      case "document_upload":
        return (
          <Fragment>
            <hr />
            {adminUploadDocumentsForm}
          </Fragment>
        );
      case "interview":
        return (
          <Fragment>
            <hr />
            <AppointmentForm
              stage={initialStage}
              setApplicanttHit={setApplicanttHit}
              showNotification={showNotification}
              toggle={toggle}
            />
          </Fragment>
        );
      default:
        return null;
    }
  };

  const drawCustomFieldsForm = () => {
    if (Array.isArray(initialStage.custom_fields) && initialStage.custom_fields.length > 0) {
      return (
        <Fragment>
          <hr />
          <CustomFieldsForm
            ref={customFieldsRef}
            key={JSON.stringify(initialStage.metadata || initialStage.custom_fields_data)}
            courseId={courseId}
            postulationBasisId={postulationId}
            stageId={initialStage.id}
            customFields={initialStage.custom_fields || []}
            initialValues={initialStage.custom_fields_data || initialStage.metadata || {}}
            setApplicanttHit={setApplicanttHit}
            showNotification={showNotification}
          />
        </Fragment>
      );
    }
    return null;
  };

  if (isEditing) {
    return (
      <>
        <Row>
          <Col md="12">
            <h5 className="small fw-bold text-muted">Candidato</h5>
            <Card className="border-0 bg-light">
              <CardBody>
                <ApplicantData user={applicant.user} applicant={applicant} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {drawCustomStageForm()}
        {drawCustomFieldsForm()}

        <hr />
        <Row>
          <Col md="12">
            <h5 className="small fw-bold text-muted">Resultados de la Etapa</h5>
            <FieldWrapper>
              <Label for="observations">Observaciones</Label>
              <Input
                type="textarea"
                id="observations"
                value={observations}
                onChange={e => setObservations(e.target.value)}
              />
            </FieldWrapper>
          </Col>
        </Row>

        <Row className="mt-3 text-center">
          <Col md="12">
            <FormGroup tag="fieldset">
              <FieldWrapper errors={aasmErrors}>
                <FormGroup check inline>
                  <Label check htmlFor="aasmStateCheck1">
                    <Input
                      id="aasmStateCheck1"
                      name="aasm_state"
                      type="radio"
                      value="approved"
                      checked={switchState === "approved"}
                      onChange={e => setSwitchState(e.target.value)}
                    />
                    Aprobar
                  </Label>
                </FormGroup>
                <FormGroup check inline>
                  <Label check htmlFor="aasmStateCheck2">
                    <Input
                      id="aasmStateCheck2"
                      name="aasm_state"
                      type="radio"
                      value="rejected"
                      checked={switchState === "rejected"}
                      onChange={e => setSwitchState(e.target.value)}
                    />
                    Rechazar
                  </Label>
                </FormGroup>
              </FieldWrapper>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={{ offset:4, size:4 }}>
            <Button
              color="success"
              block
              outline
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Guardando…
                </>
              ) : "Guardar Cambios"}
            </Button>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <Fragment>
      {!isEditing && (
        <a style={{ cursor: "pointer", color: "#006f5c" }} onClick={toggle}>
          {initialStage.name}
        </a>
      )}
      <Modal isOpen={modal} toggle={toggle} size="lg" fade={false}>
        <ModalHeader className="gradient-bg text-white" toggle={toggle}>
          Gestionar Etapa de {initialStage.name}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12">
              <h5 className="small fw-bold text-muted">Candidato</h5>
              <Card className="border-0 bg-light">
                <CardBody>
                  <ApplicantData user={applicant.user} />
                </CardBody>
              </Card>
            </Col>
          </Row>
          {drawCustomStageForm()}
          {drawCustomFieldsForm()}
          <hr />
          <Row>
            <Col md="12">
              <h5 className="small fw-bold text-muted">Resultados de la Etapa</h5>
              <FieldWrapper>
                <Label for="observations">Observaciones</Label>
                <Input
                  type="textarea"
                  name="text"
                  id="observations"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                />
              </FieldWrapper>
            </Col>
          </Row>
          <Row className="mt-3 text-center">
            <Col md="12">
              <FormGroup tag="fieldset">
                <FieldWrapper errors={aasmErrors}>
                  <FormGroup check inline>
                    <Label check htmlFor="aasmStateCheck1">
                      <Input
                        id="aasmStateCheck1"
                        name="aasm_state"
                        type="radio"
                        value="approved"
                        checked={switchState === "approved"}
                        onChange={e => setSwitchState(e.target.value)}
                      />
                      Aprobar
                    </Label>
                  </FormGroup>
                  <FormGroup check inline>
                    <Label check htmlFor="aasmStateCheck2">
                      <Input
                        id="aasmStateCheck2"
                        name="aasm_state"
                        type="radio"
                        value="rejected"
                        checked={switchState === "rejected"}
                        onChange={e => setSwitchState(e.target.value)}
                      />
                      Rechazar
                    </Label>
                  </FormGroup>
                </FieldWrapper>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={{ offset: 4, size: 4 }}>
              <Button color="success" block outline onClick={handleSubmit}>
                Guardar Cambios
              </Button>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="gradient-bg"></ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default StagesForm;
